import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRbac } from "@/hooks/rbac";
import { useTranslation } from "react-i18next";
import { CollectionPageChildProps } from "@/pages/collection/CollectionPage";
import {
  getNftsOfCollectionPaginated,
  iNft,
  NFT_SELL_STATUS,
} from "@/services/nft";
import NftsToCheckDatatable from "@/pages/collection/NftsToCheckDatatable";
import { H3 } from "../typography/Typography";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { TriangleAlert } from "lucide-react";
import { Skeleton } from "../ui/skeleton";
import TaksTab from "@/pages/collection/taks/TaksTab";
import getLink, { LINK_TYPE } from "@/lib/links";
import ErrorPage from "../errors/ErrorPage";
import { SidebarProvider, SidebarTrigger, useSidebar } from "../ui/sidebar";
import DashboardSidebar from "../layout/DashboardSidebar";

function SidebarOutsideTrigger() {
  const { open } = useSidebar();

  if (open) return null;

  return (
    <div className="fixed hidden md:block">
      <SidebarTrigger />
    </div>
  );
}

export default function CollectionTabsWrapper({
  group,
  collection,
  loading,
  onRefresh,
}: CollectionPageChildProps) {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { isSuperAdmin } = useRbac();
  const [nftsToCheck, setNftsToCheck] = useState<iNft[]>([]);
  const { "*": currentTab } = useParams<{
    "*": string;
  }>();

  const [tab, setTab] = useState(currentTab);
  useEffect(() => {
    setTab(currentTab);
  }, [currentTab]);

  const loadNftsPendingSale = async () => {
    if (!collection) return;

    try {
      const nfts = await getNftsOfCollectionPaginated({
        collectionId: collection._id,
        page: 1,
        size: 500,
        query: { sellStatus: NFT_SELL_STATUS.PENDING_SALE },
      });
      setNftsToCheck(nfts.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    loadNftsPendingSale();
  }, [collection]);

  const handleTabChange = (value: string) => {
    switch (value) {
      case "analytics":
        setTab("analytics");
        navigate(`analytics`);
        break;
      case "taks":
        setTab("taks");
        navigate(`taks`);
        break;
      case "loyalty":
        setTab("loyalty");
        navigate(`loyalty`);
        break;
      case "buybacks":
        setTab("buybacks");
        navigate(`buybacks`);
        break;
      case "payments":
        setTab("payments");
        navigate(`payments`);
        break;
      case "saleEvents":
        setTab("saleEvents");
        navigate(`saleEvents`);
        break;
      default:
        navigate(`taks`);
    }
  };

  if (loading)
    return (
      <div>
        <div className="flex flex-col space-y-3">
          <Skeleton className="bg-gray-200 h-[32px] w-[730px] rounded-lg" />
          <Skeleton className="bg-gray-200 h-[32px] rounded-lg" />
          <Skeleton className="bg-gray-200 h-[122px] rounded-lg" />
        </div>
      </div>
    );
  if (!collection || !group) {
    if (!isSuperAdmin && !loading) {
      navigate(getLink(LINK_TYPE.HOME));
      // We'll clear the cookie once navigation is stable in a future update
      document.cookie =
        "lastVisitedCollection=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      return null;
    } else {
      return <ErrorPage message={t("collection.not_found")} />;
    }
  }

  return (
    <>
      <SidebarProvider defaultOpen={true}>
        <DashboardSidebar
          collection={collection}
          group={group}
          onRefresh={onRefresh}
        />
        <main className="w-full">
          <SidebarOutsideTrigger />
          <div className=" w-full mt-4 md:mt-0 md:px-10">
            <div className="py-4 px-5 gap-2 md:gap-8 md:px-14 md:py-8">
              {nftsToCheck.length ? (
                <div className="p-4 my-4 border rounded-lg">
                  <H3>{t("collection.nft_check_put_for_sale")}</H3>
                  <div>
                    <Alert className="mt-5">
                      <TriangleAlert className="h-4 w-4" color="orange" />
                      <AlertDescription>
                        {t("collection.nft_check_put_for_sale_text_2")}
                      </AlertDescription>
                    </Alert>
                  </div>
                  <NftsToCheckDatatable collection={collection} />
                </div>
              ) : null}
              <Tabs
                defaultValue="taks"
                className=""
                onValueChange={handleTabChange}
                value={tab}
              >
                {/* <div className="flex gap-4 flex-col md:flex-row justify-between md:items-center">
                  <div className="flex items-center gap-4">
                    {!collection.isActive ? (
                      <Button
                        onClick={() => {
                          window.open(
                            "https://meetings-eu1.hubspot.com/simona-graziani/calendario-sales-per-pagina-organica",
                            "_blank",
                          );
                        }}
                        variant="gradient"
                      >
                        {t("membership.upgrade_button")}
                      </Button>
                    ) : null}
                  </div>
                </div> */}
                <TabsContent value="taks">
                  <TaksTab collection={collection} />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </main>
      </SidebarProvider>
    </>
  );
}
